import { compareFractionalIndices } from "@nova/serializer/src/client/util/fractional_indexing"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectOrthoFlightsMap } from "features/entity-caching/flights/selectors"
import { selectOrthoMasks } from "features/entity-caching/ortho-masks/selectors"
import { selectActivatedFireId } from "features/map/control/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import store from "integrations/redux/store"
import { get, isDefined } from "nodash"
import { selectOrthoComparison } from "../OrthoComparisonSlice"
import {
    selectOrthoJobByFlightId,
    selectVisibleActiveOrthophotos,
    selectOrthoOpacityMap,
} from "../OrthomosaicSlice"
import { CogOrthoLayer } from "./CogOrthoLayer"
import { LegacyOrthoLayer } from "./LegacyOrthoLayer"

export const useRGBOrthoLayer = () => {
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const fireId = useAppSelector(selectActivatedFireId)
    const flights = useAppSelector(selectOrthoFlightsMap)
    const activeOrthophotos = useAppSelector(selectVisibleActiveOrthophotos).toSorted((a, b) =>
        compareFractionalIndices(flights[a].index, flights[b].index)
    )
    const opacityMap = useAppSelector(selectOrthoOpacityMap)
    const comparison = useAppSelector(selectOrthoComparison)
    const orthoMasks = useAppSelector(selectOrthoMasks)
    const orthoMaskEditingFlightId = useAppSelector((state) => state["ortho-mask"]?.editingFlightId)
    // The active comparison flight renders at OrthoComparisonBoost via useOrthoComparisonBoostLayer
    const instances = comparison.flightId
        ? activeOrthophotos.filter((id) => id !== comparison.flightId)
        : activeOrthophotos
    const state = store.getState()

    return instances
        .map((flightId) => {
            const flight = flights[flightId]
            const job = selectOrthoJobByFlightId(flightId)(state)
            const mask = orthoMasks.find((m) => m.flightId === flightId)
            const shared = {
                id: `Orthomosaic:${flightId}`,
                bbox: flight.orthoBoundingBox,
                maxZoom: get(job, "maxZoom") || 21,
                minZoom: job?.minZoom || 8,
                opacity: opacityMap[flightId] !== undefined ? opacityMap[flightId] / 100 : 1,
                maskPolygon:
                    mask?.enabled && orthoMaskEditingFlightId !== flightId
                        ? (mask.polygon?.coordinates ?? null)
                        : null,
            }

            const extraFeatures = flight.job?.extraFeatures
            const cogId = flight.cogOrtho?.fileId

            if (!extraFeatures?.includes("cog"))
                return LegacyOrthoLayer({
                    ...shared,
                    fileroot: `/api/organization/${workspaceId}/project/${fireId}/chapter/asd/flight/${flight.demoId || flightId}/orthomosaicTiles`,
                })
            if (extraFeatures.includes("cogId") && !cogId) return null

            return CogOrthoLayer({
                ...shared,
                orgId: workspaceId || "",
                projectId: fireId || "",
                flightId: flight.demoId || flightId || "",
                isDemo: !!flight.demoId,
                cogId,
            })
        })
        .filter(isDefined)
}
